import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Calendar, Clock, Plus, Trash2, Loader2, Check, X, Edit2, Users,
  Mail, Phone, Bell, Settings, Link as LinkIcon, Copy
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';
import { publicUrl } from '../../lib/publicUrl';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const STATUS_COLORS = {
  confirmed: 'text-[#00CC99]', pending_payment: 'text-amber-400',
  completed: 'text-[#3B82F6]', cancelled: 'text-[#FF6B6B]', no_show: 'text-slate-500'
};
const PAYMENT_LABELS = { prepay: 'Pay to Book', in_person: 'Pay in Person', free: 'Free' };
const PAYMENT_COLORS = { prepay: '#635BFF', in_person: '#F59E0B', free: '#00CC99' };

export default function DashboardBookings() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('bookings');
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [reminders, setReminders] = useState({});
  const [gcalStatus, setGcalStatus] = useState({ connected: false, configured: false });
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [editService, setEditService] = useState(null);
  const [serviceForm, setServiceForm] = useState({ name: '', duration: 30, price: 0, payment_type: 'free', description: '' });
  const [loading, setLoading] = useState(false);
  const [siteId, setSiteId] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    // Get site_id from token
    try { const p = JSON.parse(atob(token.split('.')[1])); setSiteId(p.site_id || ''); } catch {}
    fetchAll();
    if (searchParams.get('gcal') === 'connected') toast.success('Google Calendar connected!');
    if (searchParams.get('gcal') === 'error') toast.error('Google Calendar connection failed');
  }, []);

  const fetchAll = () => {
    Promise.all([
      axios.get(`${API}/booking/services`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/bookings`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/booking/availability`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/booking/reminder-settings`, { headers }).catch(() => ({ data: {} })),
      axios.get(`${API}/booking/gcal-status`, { headers }).catch(() => ({ data: { connected: false, configured: false } })),
    ]).then(([svc, bk, avl, rem, gcal]) => {
      setServices(svc.data || []);
      setBookings(bk.data || []);
      setAvailability(avl.data);
      setReminders(rem.data || {});
      setGcalStatus(gcal.data);
    });
  };

  const handleSaveService = async () => {
    if (!serviceForm.name) return toast.error('Service name required');
    setLoading(true);
    try {
      if (editService) {
        await axios.put(`${API}/booking/services/${editService.id}`, serviceForm, { headers });
        toast.success('Service updated');
      } else {
        await axios.post(`${API}/booking/services`, serviceForm, { headers });
        toast.success('Service created');
      }
      setShowServiceDialog(false);
      setEditService(null);
      setServiceForm({ name: '', duration: 30, price: 0, payment_type: 'free', description: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const deleteService = async (id) => {
    if (!(await themedConfirm('Delete this service?'))) return;
    await axios.delete(`${API}/booking/services/${id}`, { headers });
    toast.success('Service deleted');
    fetchAll();
  };

  const openEditService = (s) => {
    setEditService(s);
    setServiceForm({ name: s.name, duration: s.duration, price: s.price, payment_type: s.payment_type, description: s.description });
    setShowServiceDialog(true);
  };

  const openNewService = () => {
    setEditService(null);
    setServiceForm({ name: '', duration: 30, price: 0, payment_type: 'free', description: '' });
    setShowServiceDialog(true);
  };

  const updateBookingStatus = async (id, status) => {
    await axios.put(`${API}/bookings/${id}/status`, { status }, { headers });
    toast.success(`Booking ${status}`);
    fetchAll();
  };

  const saveAvailability = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/booking/availability`, availability, { headers });
      toast.success('Availability saved');
    } catch { toast.error('Failed to save'); }
    finally { setLoading(false); }
  };

  const saveReminders = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/booking/reminder-settings`, reminders, { headers });
      toast.success('Reminder settings saved');
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const connectGoogleCal = async () => {
    try {
      const res = await axios.get(`${API}/oauth/calendar/connect`, { headers });
      window.location.href = res.data.authorization_url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Google Calendar not configured'); }
  };

  const bookingLink = siteId ? publicUrl(`/book/${siteId}`) : '';
  const copyLink = () => { navigator.clipboard.writeText(bookingLink); toast.success('Booking link copied!'); };

  const tabs = [
    { id: 'bookings', label: 'Bookings', icon: Calendar },
    { id: 'services', label: 'Services', icon: Settings },
    { id: 'availability', label: 'Availability', icon: Clock },
    { id: 'reminders', label: 'Reminders', icon: Bell },
  ];

  const upcoming = bookings.filter(b => b.status !== 'cancelled' && b.status !== 'completed').sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="bookings-title">Bookings</h1>
          <p className="text-sm text-slate-400 mt-1">Manage services, availability, and appointments</p>
        </div>
        <div className="flex items-center gap-2">
          {bookingLink && (
            <button onClick={copyLink} className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs text-[#A78BFA] bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 hover:bg-[#8B5CF6]/20 transition-colors" data-testid="copy-booking-link">
              <Copy size={12} /> Copy Booking Link
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/[0.02] p-1 rounded-2xl border border-white/5">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium transition-all ${tab === t.id ? 'bg-gradient-to-r from-[#8B5CF6]/20 to-[#3B82F6]/10 text-white border border-[#8B5CF6]/20' : 'text-slate-400 hover:text-white'}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Bookings Tab */}
      {tab === 'bookings' && (
        <div className="space-y-4" data-testid="bookings-list">
          {/* Google Calendar Status */}
          <GlassCard className="p-4 flex items-center gap-3">
            <div className={`p-2 rounded-xl ${gcalStatus.connected ? 'bg-[#00CC99]/10' : 'bg-white/5'}`}>
              <Calendar size={16} className={gcalStatus.connected ? 'text-[#00CC99]' : 'text-slate-500'} />
            </div>
            <div className="flex-1">
              <p className="text-sm text-white font-medium">Google Calendar</p>
              <p className="text-[10px] text-slate-500">{gcalStatus.connected ? 'Connected — bookings sync automatically' : gcalStatus.configured ? 'Click to connect your calendar' : 'Add GOOGLE_CLIENT_ID & SECRET to enable'}</p>
            </div>
            {!gcalStatus.connected && gcalStatus.configured && (
              <button onClick={connectGoogleCal} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#8B5CF6]/10 text-[#A78BFA] border border-[#8B5CF6]/20 hover:bg-[#8B5CF6]/20 transition-colors" data-testid="connect-gcal-btn">Connect</button>
            )}
            {gcalStatus.connected && <span className="flex items-center gap-1 text-xs text-[#00CC99]"><Check size={12} /> Synced</span>}
          </GlassCard>

          {upcoming.length > 0 ? (
            <GlassCard>
              <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
                <h3 className="text-base font-semibold text-white">Upcoming Bookings</h3>
                <span className="text-xs text-slate-500">{upcoming.length} appointment{upcoming.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="divide-y divide-white/5">
                {upcoming.map(b => (
                  <div key={b.id} className="px-6 py-4 flex items-center gap-4" data-testid={`booking-${b.id}`}>
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#8B5CF6]/20 to-[#3B82F6]/10 border border-[#8B5CF6]/20 flex flex-col items-center justify-center">
                      <span className="text-[10px] text-[#A78BFA] font-bold">{new Date(b.date + 'T00:00').toLocaleDateString('en', { month: 'short' })}</span>
                      <span className="text-sm text-white font-bold leading-none">{new Date(b.date + 'T00:00').getDate()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{b.customer_name}</p>
                      <p className="text-xs text-slate-400">{b.service_name} &middot; {b.time} &middot; {b.duration}min</p>
                      {b.customer_phone && <p className="text-[10px] text-slate-500 flex items-center gap-1"><Phone size={8} /> {b.customer_phone}</p>}
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full border" style={{ color: PAYMENT_COLORS[b.payment_type], borderColor: `${PAYMENT_COLORS[b.payment_type]}30`, background: `${PAYMENT_COLORS[b.payment_type]}10` }}>{PAYMENT_LABELS[b.payment_type]}</span>
                    <span className={`text-xs font-medium ${STATUS_COLORS[b.status] || 'text-white'}`}>{b.status.replace('_', ' ')}</span>
                    <div className="flex gap-1">
                      {b.status === 'confirmed' && <button onClick={() => updateBookingStatus(b.id, 'completed')} className="p-1.5 text-[#00CC99] hover:bg-[#00CC99]/10 rounded-lg transition-colors" title="Complete"><Check size={14} /></button>}
                      {b.status !== 'cancelled' && <button onClick={() => updateBookingStatus(b.id, 'cancelled')} className="p-1.5 text-[#FF6B6B] hover:bg-[#FF6B6B]/10 rounded-lg transition-colors" title="Cancel"><X size={14} /></button>}
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          ) : (
            <GlassCard className="p-12 text-center">
              <Calendar size={40} className="mx-auto text-slate-600 mb-3" />
              <p className="text-white font-medium mb-1">No upcoming bookings</p>
              <p className="text-sm text-slate-400">Share your booking link to start receiving appointments</p>
            </GlassCard>
          )}
        </div>
      )}

      {/* Services Tab */}
      {tab === 'services' && (
        <div className="space-y-4" data-testid="services-tab">
          <div className="flex justify-end">
            <button onClick={openNewService} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] transition-all" data-testid="add-service-btn"><Plus size={14} /> Add Service</button>
          </div>
          {services.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map(s => (
                <GlassCard key={s.id} className="p-5" data-testid={`service-card-${s.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="text-sm font-semibold text-white">{s.name}</h4>
                    <div className="flex gap-1">
                      <button onClick={() => openEditService(s)} className="p-1 text-slate-500 hover:text-white"><Edit2 size={12} /></button>
                      <button onClick={() => deleteService(s.id)} className="p-1 text-slate-500 hover:text-[#FF6B6B]"><Trash2 size={12} /></button>
                    </div>
                  </div>
                  {s.description && <p className="text-xs text-slate-400 mb-3 line-clamp-2">{s.description}</p>}
                  <div className="flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1 text-slate-300"><Clock size={10} /> {s.duration}min</span>
                    <span className="font-semibold text-white">{s.price > 0 ? `$${s.price.toFixed(2)}` : 'Free'}</span>
                    <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] border" style={{ color: PAYMENT_COLORS[s.payment_type], borderColor: `${PAYMENT_COLORS[s.payment_type]}30`, background: `${PAYMENT_COLORS[s.payment_type]}10` }}>{PAYMENT_LABELS[s.payment_type]}</span>
                  </div>
                </GlassCard>
              ))}
            </div>
          ) : (
            <GlassCard className="p-12 text-center">
              <Settings size={40} className="mx-auto text-slate-600 mb-3" />
              <p className="text-white font-medium mb-1">No services yet</p>
              <p className="text-sm text-slate-400 mb-4">Create bookable services for your clients</p>
              <button onClick={openNewService} className="px-6 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white"><Plus size={14} className="inline mr-1" /> Add Service</button>
            </GlassCard>
          )}
        </div>
      )}

      {/* Availability Tab */}
      {tab === 'availability' && availability && (
        <div className="space-y-4" data-testid="availability-tab">
          <GlassCard className="p-6">
            <h3 className="text-base font-semibold text-white mb-1">Weekly Schedule</h3>
            <p className="text-xs text-slate-400 mb-4">Set your available hours for each day</p>
            <div className="space-y-3">
              {DAYS.map(day => {
                const d = availability.schedule?.[day] || {};
                return (
                  <div key={day} className="flex items-center gap-3">
                    <label className="flex items-center gap-2 w-16">
                      <input type="checkbox" checked={d.enabled || false} onChange={(e) => setAvailability(a => ({...a, schedule: {...a.schedule, [day]: {...d, enabled: e.target.checked}}}))} className="rounded" />
                      <span className="text-xs text-white font-medium">{DAY_LABELS[day]}</span>
                    </label>
                    {d.enabled ? (
                      <div className="flex items-center gap-2">
                        <Input type="time" value={d.start || '09:00'} onChange={(e) => setAvailability(a => ({...a, schedule: {...a.schedule, [day]: {...d, start: e.target.value}}}))} className="bg-white/5 border-white/10 text-white rounded-lg text-xs w-28" />
                        <span className="text-slate-500 text-xs">to</span>
                        <Input type="time" value={d.end || '17:00'} onChange={(e) => setAvailability(a => ({...a, schedule: {...a.schedule, [day]: {...d, end: e.target.value}}}))} className="bg-white/5 border-white/10 text-white rounded-lg text-xs w-28" />
                      </div>
                    ) : <span className="text-xs text-slate-500">Closed</span>}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-white/5">
              <Label className="text-slate-300 text-xs">Default Slot Duration (minutes)</Label>
              <Input type="number" value={availability.slot_duration || 30} onChange={(e) => setAvailability(a => ({...a, slot_duration: parseInt(e.target.value) || 30}))} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg w-24" />
            </div>
            <button onClick={saveAvailability} disabled={loading} className="mt-4 px-6 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all flex items-center gap-2" data-testid="save-availability-btn">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
            </button>
          </GlassCard>
        </div>
      )}

      {/* Reminders Tab */}
      {tab === 'reminders' && (
        <div className="space-y-4" data-testid="reminders-tab">
          <GlassCard className="p-6">
            <h3 className="text-base font-semibold text-white mb-1">Appointment Reminders</h3>
            <p className="text-xs text-slate-400 mb-4">Keep clients informed about their upcoming appointments</p>
            <div className="space-y-5">
              <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl">
                <div className="p-2 rounded-xl bg-[#8B5CF6]/10"><Mail size={16} className="text-[#A78BFA]" /></div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="flex items-center gap-2 text-sm text-white">
                      <input type="checkbox" checked={reminders.email_enabled || false} onChange={(e) => setReminders(r => ({...r, email_enabled: e.target.checked}))} className="rounded" /> Email Reminders
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-slate-400 text-xs">Send</Label>
                    <Input type="number" min={1} value={reminders.email_hours_before || 24} onChange={(e) => setReminders(r => ({...r, email_hours_before: parseInt(e.target.value) || 24}))} className="bg-white/5 border-white/10 text-white rounded-lg text-xs w-16" />
                    <Label className="text-slate-400 text-xs">hours before appointment</Label>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl">
                <div className="p-2 rounded-xl bg-[#00CC99]/10"><Phone size={16} className="text-[#00CC99]" /></div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="flex items-center gap-2 text-sm text-white">
                      <input type="checkbox" checked={reminders.sms_enabled || false} onChange={(e) => setReminders(r => ({...r, sms_enabled: e.target.checked}))} className="rounded" /> SMS Reminders
                    </label>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">Requires Twilio</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-slate-400 text-xs">Send</Label>
                    <Input type="number" min={1} value={reminders.sms_hours_before || 2} onChange={(e) => setReminders(r => ({...r, sms_hours_before: parseInt(e.target.value) || 2}))} className="bg-white/5 border-white/10 text-white rounded-lg text-xs w-16" />
                    <Label className="text-slate-400 text-xs">hours before appointment</Label>
                  </div>
                </div>
              </div>
            </div>
            <button onClick={saveReminders} disabled={loading} className="mt-4 px-6 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all flex items-center gap-2" data-testid="save-reminders-btn">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
            </button>
          </GlassCard>
        </div>
      )}

      {/* Service Dialog */}
      <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="text-lg">{editService ? 'Edit' : 'New'} Bookable Service</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="service-dialog">
            <div><Label className="text-slate-300 text-xs">Service Name</Label><Input value={serviceForm.name} onChange={e => setServiceForm(f => ({...f, name: e.target.value}))} placeholder="e.g., Haircut, Photo Session" className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" data-testid="service-name-input" /></div>
            <div><Label className="text-slate-300 text-xs">Description</Label><Textarea value={serviceForm.description} onChange={e => setServiceForm(f => ({...f, description: e.target.value}))} rows={2} placeholder="What does this service include?" className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300 text-xs">Duration (min)</Label><Input type="number" value={serviceForm.duration} onChange={e => setServiceForm(f => ({...f, duration: parseInt(e.target.value) || 30}))} className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" /></div>
              <div><Label className="text-slate-300 text-xs">Price ($)</Label><Input type="number" step="0.01" value={serviceForm.price} onChange={e => setServiceForm(f => ({...f, price: parseFloat(e.target.value) || 0}))} className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" data-testid="service-price-input" /></div>
            </div>
            <div>
              <Label className="text-slate-300 text-xs mb-2 block">Payment Type</Label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(PAYMENT_LABELS).map(([key, label]) => (
                  <button key={key} onClick={() => setServiceForm(f => ({...f, payment_type: key}))}
                    className={`py-2.5 rounded-xl text-xs font-medium transition-all border ${serviceForm.payment_type === key ? '' : 'bg-white/5 border-white/10 text-slate-400'}`}
                    style={serviceForm.payment_type === key ? { color: PAYMENT_COLORS[key], borderColor: `${PAYMENT_COLORS[key]}40`, background: `${PAYMENT_COLORS[key]}10` } : {}}
                    data-testid={`payment-type-${key}`}>{label}</button>
                ))}
              </div>
            </div>
            <button onClick={handleSaveService} disabled={loading} className="w-full py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all text-sm flex items-center justify-center gap-2" data-testid="save-service-btn">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {editService ? 'Update' : 'Create'} Service
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
